export enum OpportunityType {
  None = 0,
  FullTime = 1,
  PartTime = 2,
  StretchProject = 3,
  Mentorship = 4,
  JobShadow = 5,
}

export enum OpportunityStatus {
  None = 0,
  Draft = 1,
  Proposed = 2,
  Bid = 3,
  Live = 4,
  Archived = 6,
}

export enum CandidateStatus {
  None = 0,
  Shortlisted = 1,
  Contacted = 2,
  Rejected = 3,
  Proposed = 4,
}

// ── Shared skill items ────────────────────────────────────────────────────────

export interface OpportunityTechnologyItem {
  canonicalId: number;
  canonicalName: string;
  requiredLevel: number | null;
  requiredWeight: number;
}

export interface OpportunityCertificationItem {
  certificateId: number | null;
  certificateTitle: string | null;
  certificationFamilyId: number | null;
  certificationFamilyName: string | null;
}

export interface OpportunityLanguageItem {
  languageId: number;
  languageNativeName: string;
  requiredProficiencyId: number | null;
  requiredProficiencyName: string | null;
  requiredWeight: number;
}

// ── Position ──────────────────────────────────────────────────────────────────

export interface OpportunityPositionItem {
  id: number;
  title: string;
  description: string | null;
  headCount: number;
  sortOrder: number;
  technologies: OpportunityTechnologyItem[];
  certifications: OpportunityCertificationItem[];
  languages: OpportunityLanguageItem[];
}

// ── User ref ─────────────────────────────────────────────────────────────────

export interface OpportunityUserRef {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

// ── List item (board/card) ────────────────────────────────────────────────────

export interface PositionSummary {
  id: number;
  title: string;
  headCount: number;
  topTechNames: string[];
  topCertNames: string[];
}

export interface OpportunityListItem {
  id: number;
  title: string;
  description: string | null;
  opportunityType: OpportunityType;
  opportunityStatus: OpportunityStatus;
  requiredAvailabilityDate: string | null;
  timeCommitmentPercent: number | null;
  targetOfficeName: string | null;
  positionCount: number;
  totalHeadCount: number;
  requiredTechCount: number;
  createdByFirstName: string | null;
  createdByLastName: string | null;
  createdAt: string;
  positionSummaries: PositionSummary[];
}

// ── Detail ────────────────────────────────────────────────────────────────────

export interface OpportunityDetail {
  id: number;
  title: string;
  description: string | null;
  opportunityType: OpportunityType;
  opportunityStatus: OpportunityStatus;
  requiredAvailabilityDate: string | null;
  timeCommitmentPercent: number | null;
  targetOfficeLocationId: number | null;
  targetOfficeName: string | null;
  availabilityWeightPercent: number | null;
  createdByUser: OpportunityUserRef;
  createdAt: string;
  updatedByUser: OpportunityUserRef | null;
  updatedAt: string;
  positions: OpportunityPositionItem[];
}

// ── Shortlist ─────────────────────────────────────────────────────────────────

export interface ShortlistEntry {
  personId: number;
  firstName: string;
  lastName: string;
  email: string;
  photoUrl: string | null;
  positionId: number;
  positionTitle: string;
  positionHeadCount: number;
  candidateStatus: CandidateStatus;
  notes: string | null;
  addedByManagerUserId: number;
  addedByManagerFirstName: string | null;
  addedByManagerLastName: string | null;
  addedByManagerEmail: string;
  addedAt: string;
  updatedAt: string;
  fitScoreAtShortlist: number | null;
}

// ── Requests ──────────────────────────────────────────────────────────────────

export interface OpportunityTechnologyRequest {
  technologyCanonicalId: number;
  requiredLevel: number | null;
  requiredWeight: number;
}

export interface OpportunityCertificationRequest {
  certificateId: number | null;
  certificationFamilyId: number | null;
}

export interface OpportunityLanguageRequest {
  languageId: number;
  requiredProficiencyId: number | null;
  requiredWeight: number;
}

export interface PositionRequest {
  title: string;
  description: string | null;
  headCount: number;
  sortOrder: number;
  technologies: OpportunityTechnologyRequest[];
  certifications: OpportunityCertificationRequest[];
  languages: OpportunityLanguageRequest[];
}

export interface CreateOpportunityRequest {
  title: string;
  description: string | null;
  opportunityType: OpportunityType;
  opportunityStatus: OpportunityStatus;
  requiredAvailabilityDate: string | null;
  timeCommitmentPercent: number | null;
  targetOfficeLocationId: number | null;
  availabilityWeightPercent: number | null;
  positions: PositionRequest[];
}

export interface UpdateOpportunityRequest {
  title: string;
  description: string | null;
  opportunityType: OpportunityType;
  opportunityStatus: OpportunityStatus;
  requiredAvailabilityDate: string | null;
  timeCommitmentPercent: number | null;
  targetOfficeLocationId: number | null;
  availabilityWeightPercent: number | null;
}

export interface UpsertShortlistEntryRequest {
  personId: number;
  status: CandidateStatus;
  notes: string | null;
  fitScore?: number | null;
}

// ── Candidate Fit Scoring ─────────────────────────────────────────────────────

export enum CandidateAvailabilityStatus {
  Available = 1,
  AvailableSoon = 2,
  Unavailable = 3,
  Unknown = 4,
}

export interface AppliedWeights {
  techWeightPercent: number;
  languageWeightPercent: number;
  certificationWeightPercent: number;
  availabilityWeightPercent: number;
  techCoverageSubWeightPercent: number;
  techQualitySubWeightPercent: number;
}

export interface MatchedTechnology {
  canonicalId: number;
  canonicalName: string;
  actualLevel: number | null;
  requiredLevel: number | null;
  requiredWeight: number;
  traversalWeight: number;
  matchedViaNames: string[];
}

export interface CanonicalRef {
  canonicalId: number;
  canonicalName: string;
  requiredLevel: number | null;
  requiredWeight: number;
}

export interface MatchedLanguage {
  languageId: number;
  languageName: string;
  actualProficiency: string | null;
  requiredProficiency: string | null;
  requiredWeight: number;
}

export interface LangRef {
  languageId: number;
  languageName: string;
  requiredProficiency: string | null;
  requiredWeight: number;
}

export interface CertSatisfiedRow {
  certificateId: number | null;
  certificateName: string | null;
  familyId: number | null;
  familyName: string | null;
}

export interface CertRequirementRow {
  certificateId: number | null;
  certificateName: string | null;
  familyId: number | null;
  familyName: string | null;
}

export interface CertificationGap {
  unmetRequirements: CertRequirementRow[];
  suggestion: string;
}

export interface CandidateFitResult {
  personId: number;
  firstName: string;
  lastName: string;
  email: string;
  thumbnailBase64: string | null;
  positionId: number;
  positionTitle: string;
  fitScore: number;

  techScorePercent: number | null;
  techCoveragePercent: number | null;
  techQualityPercent: number | null;
  langScorePercent: number | null;
  certScorePercent: number | null;
  availabilityScorePercent: number;

  appliedWeights: AppliedWeights;

  matchedRequiredTechnologies: MatchedTechnology[];
  missingRequiredTechnologies: CanonicalRef[];
  bonusMatchedTechnologies: MatchedTechnology[];

  certificationSatisfied: boolean;
  matchedCertifications: CertSatisfiedRow[];
  unmatchedCertifications: CertRequirementRow[];
  certificationGap: CertificationGap | null;

  matchedRequiredLanguages: MatchedLanguage[];
  missingRequiredLanguages: LangRef[];
  bonusMatchedLanguages: MatchedLanguage[];

  availabilityStatus: CandidateAvailabilityStatus;
  availabilityDate: string | null;
  officeLocation: string | null;

  scoredAt: string;
  scoringRationale: string;
}

export interface CandidateFitPagedResult {
  items: CandidateFitResult[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
